/**
 * API Ingest Playground Tab
 *
 * Run pipeline ingest with preview of raw + IKU output.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { PlayArrow as PlayArrowIcon, Refresh as RefreshIcon } from '@mui/icons-material';

import apiManagerApi from './api';

type ApiItem = {
  api_id: string;
  api_name?: string;
  provider?: string;
  base_url?: string;
  status?: string;
};

type PipelineItem = {
  pipeline_id: string;
  api_id: string;
  endpoint_id: string;
  schedule?: { every_seconds?: number };
  request?: any;
  status?: string;
  last_error?: string | null;
  last_run_at?: string | null;
  last_success_at?: string | null;
};

export default function PlaygroundTab() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apis, setApis] = useState<ApiItem[]>([]);
  const [pipelines, setPipelines] = useState<PipelineItem[]>([]);
  const [selectedApiId, setSelectedApiId] = useState('');
  const [selectedPipelineId, setSelectedPipelineId] = useState('');
  const [runResult, setRunResult] = useState<any>(null);
  const [previewRaw, setPreviewRaw] = useState<any>(null);
  const [previewIku, setPreviewIku] = useState<any>(null);
  const [previewMeta, setPreviewMeta] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  const selectedApi = useMemo(
    () => apis.find((api) => api.api_id === selectedApiId) || null,
    [apis, selectedApiId]
  );

  const selectedPipeline = useMemo(
    () => pipelines.find((pipe) => pipe.pipeline_id === selectedPipelineId) || null,
    [pipelines, selectedPipelineId]
  );

  const loadApis = async () => {
    const response = await apiManagerApi.listApis({ limit: 500 });
    const items = Array.isArray(response?.data?.apis) ? response.data.apis : [];
    setApis(items);
  };

  const loadPipelines = async (apiId?: string) => {
    const response = await apiManagerApi.listPipelines({ api_id: apiId || undefined });
    const items = Array.isArray(response?.data?.pipelines) ? response.data.pipelines : [];
    setPipelines(items);
    if (!items.find((pipe) => pipe.pipeline_id === selectedPipelineId)) {
      setSelectedPipelineId(items[0]?.pipeline_id || '');
    }
  };

  const refreshAll = async () => {
    setLoading(true);
    setError(null);
    try {
      await loadApis();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load APIs');
    } finally {
      setLoading(false);
    }
  };

  const handleRun = async () => {
    if (!selectedPipelineId) return;
    setLoading(true);
    setError(null);
    setRunResult(null);
    setPreviewRaw(null);
    setPreviewIku(null);
    setPreviewMeta(null);
    try {
      // Debug auth state
      const auth = localStorage.getItem('dc.auth');
      if (!auth) {
        setError('Not authenticated. Please login first.');
        setLoading(false);
        return;
      }
      
      const authData = JSON.parse(auth);
      if (!authData.token) {
        setError('No authentication token found. Please login again.');
        setLoading(false);
        return;
      }
      
      const response = await apiManagerApi.runPipelinePlayground(selectedPipelineId);
      const data = response?.data || {};
      const result = data.result || {};
      const preview = data.preview || result.preview || null;
      setRunResult(result);
      setPreviewRaw(preview?.raw ?? null);
      setPreviewIku(preview?.iku ?? null);
      setPreviewMeta({
        http_status: result.http_status ?? null,
        processing_time_ms: result.processing_time_ms ?? null,
        endpoint_id: result.endpoint_id ?? selectedPipeline?.endpoint_id ?? null,
        api_id: result.api_id ?? selectedPipeline?.api_id ?? null,
        upstream: result.upstream ?? null,
        status: data?.pipeline?.status ?? null,
        count: preview?.count ?? null,
      });
      await loadPipelines(selectedApiId || undefined);
    } catch (e: any) {
      // Enhanced error reporting
      const errorData = e?.response?.data;
      const errorMsg = errorData?.message || errorData?.error || e?.message || 'Playground run failed';
      const statusCode = e?.response?.status;
      
      let detailedError = errorMsg;
      if (statusCode === 400 && errorData?.error === 'missing_tenant_header') {
        detailedError = 'Missing x-tenant-id header. This is a bug - please report it.';
      } else if (statusCode === 400 && errorData?.error === 'missing_app_header') {
        detailedError = 'Missing x-app-id header. This is a bug - please report it.';
      } else if (statusCode === 401) {
        detailedError = 'Authentication failed. Please login again.';
      } else if (statusCode === 403) {
        detailedError = 'Permission denied. You need ops or builder role.';
      }
      
      // Log full error for debugging
      console.error('Pipeline run error:', {
        status: statusCode,
        errorData,
        fullError: e
      });
      
      setError(detailedError);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEndpoint = async () => {
    if (!selectedApiId || !selectedPipeline) return;
    const endpointId = selectedPipeline.endpoint_id;
    if (!endpointId) return;
    if (!confirm(`Delete endpoint ${endpointId} and its pipelines/mappings? This cannot be undone.`)) return;
    setDeleting(true);
    setError(null);
    try {
      await apiManagerApi.deleteEndpoint(selectedApiId, endpointId);
      await loadPipelines(selectedApiId);
      await loadApis();
      setSelectedPipelineId('');
      setRunResult(null);
      setPreviewRaw(null);
      setPreviewIku(null);
      setPreviewMeta(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to delete endpoint');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    refreshAll().catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadPipelines(selectedApiId || undefined).catch((e: any) => {
      setError(e?.response?.data?.error || e?.message || 'Failed to load pipelines');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApiId]);

  return (
    <Box sx={{ px: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6">API Ingest Playground</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={refreshAll}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Run a pipeline using its stored config and preview raw payload + IKU output.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            1) Select API + Pipeline
          </Typography>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
            <FormControl fullWidth>
              <InputLabel>External API</InputLabel>
              <Select
                value={selectedApiId}
                label="External API"
                onChange={(e) => setSelectedApiId(String(e.target.value))}
              >
                {apis.map((api) => (
                  <MenuItem key={api.api_id} value={api.api_id}>
                    {api.api_name || api.api_id} {api.provider ? `• ${api.provider}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={!selectedApiId}>
              <InputLabel>Pipeline</InputLabel>
              <Select
                value={selectedPipelineId}
                label="Pipeline"
                onChange={(e) => setSelectedPipelineId(String(e.target.value))}
              >
                {pipelines.map((pipe) => (
                  <MenuItem key={pipe.pipeline_id} value={pipe.pipeline_id}>
                    {pipe.endpoint_id} {pipe.status ? `• ${pipe.status}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} /> : <PlayArrowIcon />}
              disabled={loading || !selectedPipelineId}
              onClick={handleRun}
            >
              Run now
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={loading || deleting || !selectedPipelineId}
              onClick={handleDeleteEndpoint}
              sx={{ ml: 2 }}
            >
              {deleting ? 'Deleting…' : 'Delete endpoint'}
            </Button>
          </Box>

          {selectedApi && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Base URL: {selectedApi.base_url || '—'} • Status: {selectedApi.status || '—'}
              </Typography>
            </Box>
          )}

          {selectedPipeline && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Endpoint: {selectedPipeline.endpoint_id} • Every {selectedPipeline.schedule?.every_seconds || '—'}s
              </Typography>
              {selectedPipeline.last_error && (
                <Typography variant="body2" color="error">
                  Last error: {selectedPipeline.last_error}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            2) Preview output
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Paper sx={{ p: 2, bgcolor: 'grey.100', minHeight: 220, overflow: 'auto' }}>
              <Typography variant="caption" color="text.secondary">
                Raw payload
              </Typography>
              <pre style={{ margin: 0 }}>{previewRaw ? JSON.stringify(previewRaw, null, 2) : '—'}</pre>
            </Paper>
            <Paper sx={{ p: 2, bgcolor: 'success.light', minHeight: 220, overflow: 'auto' }}>
              <Typography variant="caption" color="text.secondary">
                Mapped IKU
              </Typography>
              <pre style={{ margin: 0 }}>{previewIku ? JSON.stringify(previewIku, null, 2) : '—'}</pre>
            </Paper>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            3) Run metadata
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'grey.100', overflow: 'auto' }}>
            <pre style={{ margin: 0 }}>
              {previewMeta ? JSON.stringify(previewMeta, null, 2) : '—'}
            </pre>
          </Paper>
          {runResult && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Full result
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 320, overflow: 'auto' }}>
                <pre style={{ margin: 0 }}>{JSON.stringify(runResult, null, 2)}</pre>
              </Paper>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

